import { Box } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import type { Conversation } from 'lib-shared-types-api';
import isEqual from 'lodash/isEqual';
import { ComponentProps, memo, useMemo } from 'react';

import { useConversationHasMissingRequiredCustomFieldsInAcw } from 'utils/conversation';

import styles from './conversationStatusColorBox.module.scss';

type ConversationStatusColorBoxProps = {
  /* The conversation id */
  csid: Conversation['id'];
  /* The conversation status */
  status: Conversation['status'];
} & Omit<ComponentProps<typeof Box>, 'background' | 'children'>;

/**
 * Represents a component for displaying the status color of a given
 * conversation. The component can be shaped through {@link Box} props.
 */
export const ConversationStatusColorBox = memo(function ConversationStatus({
  csid,
  status,
  ...boxProps
}: ConversationStatusColorBoxProps): JSX.Element {
  const isActive = status === 'open';
  const hasFollowUp = status === 'pending' || status === 'awaitingpending';
  const hasMissingRequiredCustomFieldsInAcw =
    useConversationHasMissingRequiredCustomFieldsInAcw({ csid });

  const background = useMemo(() => {
    if (hasMissingRequiredCustomFieldsInAcw) {
      return 'orange.400';
    }

    if (isActive) {
      return 'green.500';
    }

    if (hasFollowUp) {
      return 'blue.400';
    }

    return 'neutral.400';
  }, [hasFollowUp, hasMissingRequiredCustomFieldsInAcw, isActive]);

  return (
    <Box
      {...boxProps}
      background={background}
      className={classNames(styles.root, boxProps.className)}
    />
  );
},
isEqual);
